import { create } from 'zustand'
import { parseazaDeepLink } from '@edrikom/shared'

export type Page =
  | 'dashboard'
  | 'clienti'
  | 'contracte'
  | 'echipamente'
  | 'rezervari'
  | 'predare-retur'
  | 'mentenanta'
  | 'facturare'
  | 'incasari'
  | 'cheltuieli'
  | 'transfer'
  | 'hardware'
  | 'rapoarte'
  | 'setari'

export interface NavEntry {
  page: Page
  params: Record<string, string>
}

interface NavState {
  page: Page
  params: Record<string, string>
  history: NavEntry[]
  historyIndex: number
  navGuard: (() => boolean) | null
  blockedNav: { page: Page; params?: Record<string, string> } | null
  /**
   * Punctul de lucru declarat de deep-link-ul cu care s-a deschis aplicația (`pl=`), cât timp
   * nu a fost încă reconciliat cu sesiunea. App.tsx îl compară cu punctul sesiunii și, la
   * nepotrivire, cere comutarea în loc să deschidă documentul în baza de date greșită.
   */
  deepLinkPl: string | null

  navigate: (page: Page, params?: Record<string, string>) => void
  back: () => void
  forward: () => void
  canGoBack: () => boolean
  canGoForward: () => boolean
  setNavGuard: (guard: (() => boolean) | null) => void
  clearBlockedNav: () => void
  clearDeepLinkPl: () => void
  /** Revine la dashboard și golește istoricul (comutare punct de lucru / logout). */
  reset: () => void
}

const PAGINI_VALIDE: Page[] = [
  'dashboard', 'clienti', 'contracte', 'echipamente', 'rezervari', 'predare-retur',
  'mentenanta', 'facturare', 'incasari', 'cheltuieli', 'transfer', 'hardware', 'rapoarte', 'setari',
]

/** Deep-link din email: parsează `#/pagina?cheie=valoare&pl=<id>` la încărcare (nu aruncă niciodată). */
function navDinUrl(): NavEntry & { pl: string | null } {
  const implicit = { page: 'dashboard' as Page, params: {}, pl: null }
  if (typeof window === 'undefined') return implicit
  let link: ReturnType<typeof parseazaDeepLink> = null
  try { link = parseazaDeepLink(window.location.hash, PAGINI_VALIDE) } catch { link = null }
  if (!link) return implicit
  // Curăță hash-ul ca refresh-ul să nu re-declanșeze (starea persistă prin login).
  try { window.history.replaceState(null, '', window.location.pathname + window.location.search) } catch { /* ignore */ }
  return { page: link.page as Page, params: link.params, pl: link.pl }
}

const navInitial = navDinUrl()

export const useNav = create<NavState>((set, get) => ({
  page: navInitial.page,
  params: navInitial.params,
  history: [{ page: navInitial.page, params: navInitial.params }],
  historyIndex: 0,
  navGuard: null,
  blockedNav: null,
  deepLinkPl: navInitial.pl,

  setNavGuard: (guard) => set({ navGuard: guard }),
  clearBlockedNav: () => set({ blockedNav: null }),
  clearDeepLinkPl: () => set({ deepLinkPl: null }),
  reset: () => set({
    page: 'dashboard', params: {}, history: [{ page: 'dashboard', params: {} }], historyIndex: 0,
    navGuard: null, blockedNav: null, deepLinkPl: null,
  }),

  navigate: (page, params = {}) => {
    const { history, historyIndex, navGuard } = get()
    if (navGuard && !navGuard()) {
      set({ blockedNav: { page, params } })
      return
    }
    set({ blockedNav: null })
    const newHistory = history.slice(0, historyIndex + 1)
    const current = newHistory[newHistory.length - 1]
    if (current?.page === page && JSON.stringify(current.params) === JSON.stringify(params)) return
    newHistory.push({ page, params })
    // Navigarea explicită către ALTĂ pagină abandonează deep-link-ul spre alt punct de lucru:
    // altfel bannerul „Documentul aparține punctului X" rămânea afișat peste orice pagină aleasă
    // din meniu, iar „Comută" deschidea pagina din meniu în loc de document (revizia 5 nov).
    const parasesteDeepLink = !!get().deepLinkPl && current?.page !== page
    set({
      page, params, history: newHistory, historyIndex: newHistory.length - 1,
      ...(parasesteDeepLink ? { deepLinkPl: null } : {}),
    })
  },

  back: () => {
    const { history, historyIndex, navGuard } = get()
    if (navGuard && !navGuard()) {
      const newIndex = historyIndex - 1
      if (newIndex >= 0) set({ blockedNav: { page: history[newIndex].page, params: history[newIndex].params } })
      return
    }
    set({ blockedNav: null })
    if (historyIndex <= 0) return
    const newIndex = historyIndex - 1
    const { page, params } = history[newIndex]
    set({ page, params, historyIndex: newIndex })
  },

  forward: () => {
    const { history, historyIndex } = get()
    if (historyIndex >= history.length - 1) return
    const newIndex = historyIndex + 1
    const { page, params } = history[newIndex]
    set({ page, params, historyIndex: newIndex })
  },

  canGoBack: () => get().historyIndex > 0,
  canGoForward: () => get().historyIndex < get().history.length - 1,
}))
